import { Building2, Loader2, MapPin, Phone, Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLazySearchProviderServicesQuery } from '@/services/api/providerDirectoryV2Service';
import { useRelinkProviderServiceMutation } from '@/services/api/treatmentService';
import type { ProviderDirectoryV2SearchResult } from '@/services/types/provider-directory-v2-types';
import type { MedicalProvider } from '@/services/types/treatment-types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IdentityRelinkStepProps {
  provider: MedicalProvider;
  matterId: string;
  onRelinked: () => void;
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAddress(
  address?: {
    address_line1?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    formatted_address?: string | null;
  } | null,
): string | null {
  if (!address) return null;
  if (address.formatted_address) return address.formatted_address;
  const parts = [
    address.address_line1,
    address.city,
    address.state,
    address.postal_code,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const IdentityRelinkStep = ({
  provider,
  matterId,
  onRelinked,
  onBack,
}: IdentityRelinkStepProps) => {
  const [searchQuery, setSearchQuery] = useState(provider.name ?? '');
  const [selectedResult, setSelectedResult] =
    useState<ProviderDirectoryV2SearchResult | null>(null);

  const [triggerSearch, { data: searchData, isFetching: isSearching }] =
    useLazySearchProviderServicesQuery();

  const [relinkService, { isLoading: isRelinking }] =
    useRelinkProviderServiceMutation();

  const results = searchData?.results ?? [];

  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    triggerSearch({ q, limit: 10 });
    setSelectedResult(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelect = (result: ProviderDirectoryV2SearchResult) => {
    setSelectedResult(result);
  };

  const handleRelink = async () => {
    if (!selectedResult) return;
    try {
      await relinkService({
        matterId,
        providerId: provider.id,
        providerServiceId: selectedResult.provider_service.id,
      }).unwrap();
      toast.success('Provider re-linked to new directory entry');
      onRelinked();
    } catch {
      toast.error('Failed to re-link provider');
    }
  };

  return (
    <div className="space-y-4">
      {/* Explanation */}
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
        <p className="text-sm font-medium text-gray-900">
          Re-link Provider Service
        </p>
        <p className="mt-0.5 text-xs text-gray-600">
          Search the directory to find the correct provider service for{' '}
          <span className="font-medium">{provider.name}</span>. After
          re-linking, the identity will be reset for re-verification.
        </p>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-600" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search by provider name, organization, or address..."
            className="pl-8 text-sm"
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleSearch}
          disabled={!searchQuery.trim() || isSearching}
        >
          {isSearching ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            'Search'
          )}
        </Button>
      </div>

      {/* Search results */}
      {results.length > 0 && (
        <div className="max-h-64 space-y-1.5 overflow-y-auto">
          {results.map((result) => {
            const isSelected =
              selectedResult?.provider_service.id ===
              result.provider_service.id;
            const address = formatAddress(result.clinical_location?.address);
            const phone = result.clinical_location?.primary_phone ?? null;
            const org = result.organization?.name ?? null;

            return (
              <button
                key={result.provider_service.id}
                type="button"
                onClick={() => handleSelect(result)}
                className={`w-full rounded-md border p-2.5 text-left transition-colors ${
                  isSelected
                    ? 'border-indigo-400 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <p className="text-sm font-medium text-gray-900">
                  {result.provider_service.name}
                </p>
                {org && org !== result.provider_service.name && (
                  <div className="mt-0.5 flex items-center gap-1">
                    <Building2 className="size-3 text-gray-600" />
                    <span className="text-xs text-gray-600">{org}</span>
                  </div>
                )}
                {address && (
                  <div className="mt-0.5 flex items-center gap-1">
                    <MapPin className="size-3 text-gray-600" />
                    <span className="text-xs text-gray-600">{address}</span>
                  </div>
                )}
                {phone && (
                  <div className="mt-0.5 flex items-center gap-1">
                    <Phone className="size-3 text-gray-600" />
                    <span className="text-xs text-gray-600">{phone}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* No results */}
      {searchData && results.length === 0 && !isSearching && (
        <p className="py-4 text-center text-sm text-gray-600">
          No matching providers found. Try a different search.
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 border-t border-gray-200 pt-3">
        <Button
          size="sm"
          variant="outline"
          onClick={onBack}
          disabled={isRelinking}
        >
          Back
        </Button>
        <Button
          size="sm"
          onClick={handleRelink}
          disabled={!selectedResult || isRelinking}
        >
          {isRelinking ? (
            <Loader2 className="mr-1.5 size-3.5 animate-spin" />
          ) : null}
          Re-link to Selected
        </Button>
      </div>
    </div>
  );
};
